import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { Banknote, Bitcoin, Check } from 'lucide-react'
import { MaskLines, Reveal } from '@/components/motion/reveal'
import { CountUp } from '@/components/motion/roll-text'
import { ToneSection } from '@/components/generals/tone'
import { Cta } from '@/components/generals/cta'
import { Logo } from '@/components/generals/logo'
import { IndustryHero } from '@/components/landing/industry/industry-hero'
import { ResourceDownload } from '@/components/landing/industry/resource-download'
import { DynamicKycVisual } from '@/components/landing/shared/product-visuals'
import { Testimonials } from '@/components/landing/shared/testimonials'
import { MustHaves } from '@/components/landing/shared/must-haves'
import { clients, testimonials } from '@/constants/content'
import { resources } from '@/constants/resources'

const cryptoClients = [clients.banxa, clients.paybis, clients.gateio, clients.coinflow, clients.btcc, clients.coingate]
const cryptoVoices = testimonials.filter(item => ['Paybis.com', 'Coinflow Labs'].includes(item.company))

// "KYC reduces payment fraud" gets struck through as it scrolls into view.
const Myth: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduce = useReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.8', 'start 0.3'] })
    const strike = useTransform(scrollYProgress, [0, 0.7], reduce ? [1, 1] : [0, 1])
    const strikeSize = useTransform(strike, value => `${value * 100}% 0.06em`)
    const stamp = useTransform(scrollYProgress, [0.6, 1], reduce ? [1, 1] : [0, 1])
    const stampScale = useTransform(scrollYProgress, [0.6, 1], reduce ? [1, 1] : [1.6, 1])

    return (
        <div ref={ref} className='relative max-w-5xl py-10'>
            <p className='font-serif text-[clamp(2.4rem,6.6vw,6.75rem)] italic leading-[1.08]'>
                <motion.span
                    className='[-webkit-box-decoration-break:clone] [box-decoration-break:clone]'
                    style={{
                        backgroundImage: 'linear-gradient(#f92468, #f92468)',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '0 60%',
                        backgroundSize: strikeSize
                    }}
                >
                    “AML tools like KYC reduce payment fraud.”
                </motion.span>
            </p>
            <motion.span
                className='absolute -top-2 right-0 rotate-[-6deg] border-2 border-ember px-4 py-2 font-heading text-lg font-bold uppercase tracking-[0.24em] text-ember md:right-[8%] md:text-2xl'
                style={{ opacity: stamp, scale: stampScale }}
            >
                Myth
            </motion.span>
        </div>
    )
}

// A chain of onramp transactions verified one block after another.
const Chain: FC = (): ReactNode => {
    const reduce = useReducedMotion()
    const [step, setStep] = useState(0)
    const blocks = ['A1F3', '7C0E', 'B92D', '04FA', 'E6B1']

    useEffect(() => {
        if (reduce) return

        const timer = window.setInterval(() => setStep(value => (value + 1) % (blocks.length + 2)), 750)

        return () => {
            window.clearInterval(timer)
        }
    }, [reduce, blocks.length])

    return (
        <div aria-hidden className='w-full max-w-sm border border-tone-line bg-tone-bg/60 p-5 backdrop-blur-xl'>
            <div className='flex justify-between t-label text-tone-muted'>
                <span>
                    Fiat-to-crypto onramp
                </span>
                <span className='text-tone-accent'>
                    {Math.min(step, blocks.length)}/{blocks.length} verified
                </span>
            </div>
            <ol className='mt-6 flex flex-col'>
                {blocks.map((hash, index) => {
                    const done = reduce || index < step

                    return (
                        <li key={hash} className='relative flex items-center gap-4 pb-4 last:pb-0'>
                            {index < blocks.length - 1 && (
                                <span className='absolute left-[0.6875rem] top-6 h-[calc(100%-1rem)] w-px bg-tone-line'>
                                    <motion.span
                                        className='block w-full origin-top bg-tone-accent'
                                        animate={{ scaleY: done ? 1 : 0 }}
                                        style={{ height: '100%' }}
                                        transition={{ duration: 0.5 }}
                                    />
                                </span>
                            )}
                            <motion.span
                                className='relative flex size-6 shrink-0 items-center justify-center border border-tone-line font-mono text-[0.6rem]'
                                animate={{
                                    backgroundColor: done ? 'rgba(201,184,255,1)' : 'rgba(201,184,255,0)',
                                    color: done ? '#140c2b' : 'rgba(237,232,251,0.6)'
                                }}
                                transition={{ duration: 0.35 }}
                            >
                                {done ? <Check className='size-3.5' /> : index + 1}
                            </motion.span>
                            <span className='flex flex-1 items-center justify-between font-mono text-xs uppercase tracking-[0.1em]'>
                                <span>
                                    Block {hash}
                                </span>
                                <span className={done ? 'text-tone-accent' : 'text-tone-muted'}>
                                    {done ? 'Approved' : 'Pending'}
                                </span>
                            </span>
                        </li>
                    )
                })}
            </ol>
        </div>
    )
}

// Fiat in, crypto out, with a sub-500ms decision in between.
const Onramp: FC = (): ReactNode => {
    const nodes = [
        { label: 'Fiat', note: 'Card payment' },
        { label: 'nSure.ai', note: 'Decision under 500ms' },
        { label: 'Crypto', note: 'Delivered' }
    ]

    return (
        <div aria-hidden className='relative w-full'>
            <div className='absolute left-[8%] right-[8%] top-10 h-px bg-tone-line md:top-14'>
                {[0, 1, 2, 3].map(pulse => (
                    <span
                        key={pulse}
                        className='absolute top-1/2 h-1 w-14 -translate-y-1/2 bg-gradient-to-r from-transparent via-white to-transparent'
                        style={{ animation: `onramp-pulse 3.2s ${pulse * 0.8}s linear infinite both` }}
                    />
                ))}
            </div>

            <ol className='relative grid grid-cols-3'>
                {nodes.map((node, index) => (
                    <li key={node.label} className='flex flex-col items-center gap-5 text-center'>
                        <span className={index === 1
                            ? 'flex size-20 items-center justify-center rounded-full bg-white text-violet-deep md:size-28'
                            : 'flex size-20 items-center justify-center rounded-full border border-tone-line bg-tone-bg md:size-28'}
                        >
                            {index === 0 && <Banknote className='size-7 md:size-9' />}
                            {index === 1 && <Logo markOnly className='h-8 w-auto md:h-11' />}
                            {index === 2 && <Bitcoin className='size-7 md:size-9' />}
                        </span>
                        <span className='flex flex-col gap-1'>
                            <span className='font-heading text-xl font-semibold md:text-2xl'>
                                {node.label}
                            </span>
                            <span className='t-label text-tone-muted'>
                                {node.note}
                            </span>
                        </span>
                    </li>
                ))}
            </ol>
        </div>
    )
}

export const CryptoIndustry: FC = (): ReactNode => {
    return (
        <>
            <IndustryHero
                tone='crypto'
                accent='#c9b8ff'
                index='01'
                name='Crypto'
                word='Crypto'
                lines={[
                    'Unlocking the',
                    'power of Adaptive AI',
                    <span key='revolution'>
                        for the <span className='font-serif font-normal normal-case italic tracking-normal text-tone-accent wdth-100'>crypto revolution</span>
                    </span>
                ]}
                intro='Take a front row seat at the cutting edge of artificial intelligence and turn payment fraud into a profit center.'
                clients={cryptoClients}
                visual={<Chain />}
            />

            <ToneSection tone='paper' id='aml-tools' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <div className='grid gap-12 lg:grid-cols-12'>
                    <MaskLines
                        as='h2'
                        className='font-heading t-h1 font-semibold uppercase wdth-75 lg:col-span-7'
                        lines={['Stop using AML tools', 'to fight payment fraud.']}
                    />
                    <Reveal className='t-lead text-tone-muted lg:col-span-4 lg:col-start-9 lg:pt-3'>
                        Merchants decline up to 20% of transactions by using AML tools like KYC beyond the strictly necessary regulation, in hope that it will contribute to reducing payment fraud. This is a myth. All it does is significantly increase false positives.
                    </Reveal>
                </div>

                <div className='mt-20 lg:mt-28'>
                    <Myth />
                </div>

                <Reveal className='mt-16 flex items-center gap-4 t-label text-tone-muted'>
                    <span className='h-px w-16 bg-tone-line' />
                    Let’s look at what you stand to lose when you use the wrong tools...
                </Reveal>
            </ToneSection>

            <ToneSection tone='crypto' id='kyc-cost' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <MaskLines
                    as='h2'
                    className='font-heading t-display font-semibold uppercase wdth-75'
                    lines={['KYC is costly', <span key='crypto' className='font-serif font-normal normal-case italic tracking-normal text-tone-accent wdth-100'>in crypto.</span>]}
                />

                <dl className='mt-20 grid border-t border-tone-line md:grid-cols-3'>
                    {[
                        { value: 98, suffix: '%', label: 'of declined transactions are false positives' },
                        { value: 65, suffix: '%', label: 'of those false positives are attributed to new users' },
                        { value: 7, suffix: '×', label: 'more to acquire a new customer than to retain an existing one' }
                    ].map((stat, index) => (
                        <div key={stat.label} className={index > 0 ? 'flex flex-col gap-4 border-b border-tone-line py-10 md:border-b-0 md:border-l md:px-8' : 'flex flex-col gap-4 border-b border-tone-line py-10 md:border-b-0 md:pr-8'}>
                            <dd className='font-heading text-[clamp(5rem,10vw,9.5rem)] font-semibold leading-[0.82] tracking-[-0.05em] wdth-62'>
                                <CountUp to={stat.value} suffix={stat.suffix} />
                            </dd>
                            <dt className='max-w-[16rem] text-tone-muted'>
                                {stat.label}
                            </dt>
                        </div>
                    ))}
                </dl>

                <div className='mt-16 grid gap-10 lg:grid-cols-12'>
                    <Reveal className='t-lead text-tone-muted lg:col-span-5'>
                        In reality, KYC is often only required for a percentage of transactions. Meanwhile, 98% of declined transactions are false positives, 65% of which are attributed to new users.
                    </Reveal>
                    <Reveal className='font-serif text-[clamp(1.75rem,3vw,3rem)] italic leading-[1.1] lg:col-span-6 lg:col-start-7' delay={0.1}>
                        When it costs seven times more to acquire a new customer than it does to retain an existing one, it begs the question... what is KYC costing you?
                    </Reveal>
                </div>
            </ToneSection>

            <ToneSection tone='violet' id='onramp' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <div className='grid gap-16 lg:grid-cols-12 lg:items-center'>
                    <div className='flex flex-col gap-10 lg:col-span-5'>
                        <MaskLines
                            as='h2'
                            className='font-heading t-h1 font-semibold uppercase wdth-75'
                            lines={['Streamline your', 'fiat-to-crypto', 'onramp.']}
                        />
                        <Reveal className='t-lead text-tone-muted'>
                            Optimize user experience with nSure.ai’s best-in-class payment fraud protection solutions and using KYC only when required to streamline your fiat-to-crypto onramp, accept more transactions, and retain more users.
                        </Reveal>
                        <div>
                            <Cta href='/contact' variant='invert' cursor='Book'>
                                Book a call
                            </Cta>
                        </div>
                    </div>
                    <div className='lg:col-span-6 lg:col-start-7'>
                        <Onramp />
                    </div>
                </div>
            </ToneSection>

            <ToneSection tone='ink' id='dynamic-kyc' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <div className='grid gap-16 lg:grid-cols-12 lg:items-center'>
                    <div className='order-2 lg:order-1 lg:col-span-6'>
                        <DynamicKycVisual />
                    </div>
                    <div className='order-1 flex flex-col gap-8 lg:order-2 lg:col-span-5 lg:col-start-8'>
                        <span className='t-label text-tone-muted'>
                            DynamicKYC®
                        </span>
                        <MaskLines
                            as='h2'
                            className='font-heading t-h1 font-semibold uppercase wdth-75'
                            lines={['KYC without', <span key='tmi' className='text-tone-accent'>TMI.</span>]}
                        />
                        <Reveal className='t-lead text-tone-muted'>
                            Know Your Customer is an AML tool. As an anti-fraud measure, it leads to decline rates of up to 20%. Our data shows that only 2% of those transactions are actual payment fraud.
                        </Reveal>
                    </div>
                </div>
            </ToneSection>

            <Testimonials items={cryptoVoices} tone='paper' label='Crypto, in their words' />

            <ResourceDownload tone='void' {...resources.crypto} />

            <MustHaves tone='crypto' label='Built in' />
        </>
    )
}
